/**
 * Thin typed wrapper around window.Telegram.WebApp. This is the ONLY module
 * that should touch the global Telegram object — everything else in the app
 * consumes TelegramContext (see ./TelegramProvider.tsx) so Telegram-specific
 * concerns never leak into screens/components.
 */

export interface TelegramUser {
  id: number;
  first_name?: string;
  last_name?: string;
  username?: string;
  language_code?: string;
}

interface WebAppApi {
  initData: string;
  initDataUnsafe: {
    user?: TelegramUser;
    start_param?: string;
  };
  colorScheme: "light" | "dark";
  ready(): void;
  expand(): void;
  requestFullscreen?: () => void;
  disableVerticalSwipes?: () => void;
  setHeaderColor?: (color: string) => void;
  setBackgroundColor?: (color: string) => void;
  onEvent(event: string, handler: () => void): void;
  offEvent(event: string, handler: () => void): void;
  HapticFeedback?: {
    impactOccurred(style: "light" | "medium" | "heavy" | "rigid" | "soft"): void;
    notificationOccurred(type: "error" | "success" | "warning"): void;
  };
  viewportHeight?: number;
  viewportStableHeight?: number;
  platform?: string;
}

declare global {
  interface Window {
    Telegram?: { WebApp?: WebAppApi };
  }
}

export function getTelegramWebApp(): WebAppApi | null {
  return typeof window !== "undefined" ? window.Telegram?.WebApp ?? null : null;
}

/**
 * Whether a Telegram WebApp bridge object is present at all. This is the
 * correct gate for "are we running inside Telegram" — it must NOT depend on
 * `initData` being populated yet. Telegram Desktop in particular can expose
 * `window.Telegram.WebApp` with an empty `initData` string for a brief
 * moment (or for the whole session, on some launch paths) before/without
 * ever populating it. Gating on initData caused `ready()` to never be
 * called in that case, which leaves Telegram's own loading placeholder
 * on screen forever — the "blank/black screen" bug this guards against.
 */
export function hasTelegramWebApp(): boolean {
  return !!getTelegramWebApp();
}

function safeCall(label: string, fn: () => void): void {
  try {
    fn();
  } catch (error) {
    // Never let one unsupported/failing Telegram API call block the rest of
    // startup, and never let it throw uncaught out of an effect.
    console.warn(`[telegram] ${label} failed`, error);
  }
}

let readySignaled = false;
let rawReadySent = false;

/** Test-only: readiness is a real module-level singleton (by design — see signalTelegramReady), so tests need a way to reset it between cases. Not used by app code. */
export function __resetHandshakeForTests(): void {
  readySignaled = false;
  rawReadySent = false;
}

/**
 * Sends `web_app_ready` directly over Telegram's native transport —
 * `window.TelegramWebviewProxy` (Telegram Desktop) or `window.external.notify`
 * (older WebViews) — using the exact wire format telegram-web-app.js uses
 * internally, WITHOUT going through that script at all.
 *
 * NOT used by the normal app startup path (see signalTelegramReady below).
 * Real production traces from Telegram Desktop and Telegram iOS both showed
 * execution stopping immediately after this signal was sent early (before
 * the app's own UI had mounted) — consistent with a native WebView host
 * that pauses/discards rendering while hidden and never recovers once told
 * to reveal an unpainted view. Kept only for isolated diagnostics; do not
 * wire this into main.tsx or TelegramProvider again without new evidence
 * that the early-timing theory was wrong, not just the raw-transport idea.
 */
export function sendRawWebAppReady(): boolean {
  if (rawReadySent) return true;
  try {
    const proxy = (window as unknown as { TelegramWebviewProxy?: { postEvent: (event: string, data: string) => void } })
      .TelegramWebviewProxy;
    if (proxy && typeof proxy.postEvent === "function") {
      proxy.postEvent("web_app_ready", JSON.stringify(""));
      rawReadySent = true;
      return true;
    }
    const ext = window.external as unknown as { notify?: (data: string) => void } | undefined;
    if (ext && typeof ext.notify === "function") {
      ext.notify(JSON.stringify({ eventType: "web_app_ready", eventData: "" }));
      rawReadySent = true;
      return true;
    }
  } catch (error) {
    console.warn("[telegram] raw web_app_ready failed", error);
  }
  return false;
}

/**
 * Calls the OFFICIAL Telegram WebApp readiness sequence —
 * `window.Telegram.WebApp.ready()` followed by `expand()` and cosmetic
 * theme calls — via the bridge script (telegram-web-app.js), never the raw
 * native transport. This is the ONLY readiness signal the production app
 * sends to Telegram.
 *
 * Callers MUST NOT invoke this until real app UI has actually painted (see
 * ./readySignal.ts, triggered from WelcomeScreen) — Telegram keeps its own
 * loading overlay on top of the WebView until `ready()` fires, and calling
 * it before there is anything real to show risks revealing an unpainted or
 * still-loading view.
 *
 * `ready()` is attempted in its own try/catch so its outcome can be
 * reported precisely; every other call is individually guarded so an
 * unsupported method on a given Telegram client can never crash startup or
 * block the calls after it. Idempotent — only the first call reaches the
 * WebView.
 */
export function signalTelegramReady(): { attempted: boolean; readyOk: boolean } {
  if (readySignaled) return { attempted: true, readyOk: true };
  const webApp = getTelegramWebApp();
  if (!webApp) return { attempted: false, readyOk: false };
  readySignaled = true;

  let readyOk = true;
  try {
    webApp.ready();
  } catch (error) {
    readyOk = false;
    console.warn("[telegram] ready() failed", error);
  }
  safeCall("expand()", () => webApp.expand());
  safeCall("disableVerticalSwipes()", () => webApp.disableVerticalSwipes?.());
  safeCall("setBackgroundColor()", () => webApp.setBackgroundColor?.("#15130f"));
  safeCall("setHeaderColor()", () => webApp.setHeaderColor?.("#15130f"));

  return { attempted: true, readyOk };
}

export function hapticImpact(style: "light" | "medium" | "heavy" = "light"): void {
  safeCall("HapticFeedback.impactOccurred()", () => getTelegramWebApp()?.HapticFeedback?.impactOccurred(style));
}

export function hapticNotification(type: "error" | "success" | "warning"): void {
  safeCall("HapticFeedback.notificationOccurred()", () => getTelegramWebApp()?.HapticFeedback?.notificationOccurred(type));
}
